import { useMutation, useQueryClient } from '@tanstack/react-query';
import { can, plannedWithSpare } from '@pl/domain';
import type { BoxStickerSize, LabelBatchDto, MeDto, PalletStickerSize, SkuDto, StickerKind } from '@pl/domain';
import { useId, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { stickerInches, zonedDateTime } from '../../lib/format';
import { qk, useBatches, useMe, useOrg, usePlannedQuantity, useSkuMap, useSkus } from '../../lib/queries';
import { Chip, Empty, ErrorNote, RefetchNote, SiteTag, SkuTag, Spinner } from '../../ui/bits';
import { Modal } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import { BlockedPdfLink, openPdf, type OpenedPdf } from '../PdfLink';
import { SkuPicker } from './SkuPicker';
import { StickerPreview } from './StickerPreview';
import s from './Stickers.module.css';

const KINDS: { value: StickerKind; label: string }[] = [
  { value: 'sku', label: 'SKU stickers' },
  { value: 'blank', label: 'Blank' },
  { value: 'pallet', label: 'Pallet' },
];

const KIND_WORDS: Record<StickerKind, string> = { sku: 'SKU', blank: 'Blank', pallet: 'Pallet' };

/** `POST /label-batches` caps quantity at 1000, or 200 for pallets. */
const maxQtyFor = (kind: StickerKind) => (kind === 'pallet' ? 200 : 1000);

/**
 * The company's sticker sizes, from Settings → Printing by way of `MeDto.org`. Read defensively: a
 * phone or laptop can boot from an identity cached before the sizes were part of it, and a size
 * this build cannot draw falls back to the default roll, which is also what the printer does.
 */
function orgSizes(org: MeDto['org'] | undefined): { box: BoxStickerSize; pallet: PalletStickerSize } {
  const o = org as { stickerSize?: unknown; palletStickerSize?: unknown } | undefined;
  return { box: o?.stickerSize === '4x2' ? '4x2' : '2.25x1.25', pallet: o?.palletStickerSize === '4x2' ? '4x2' : '4x6' };
}

/** Where a success line belongs: next to the action that produced it. */
type ResultAt = 'form' | 'printer' | 'batches';

interface PrintResult {
  at: ResultAt;
  text: string;
  /** Batch prints say the PDF opened; the test label's own sentence already says what to do. */
  withTab: boolean;
  /** The PDF as handed to a tab, or null when it could not be fetched. */
  pdf: OpenedPdf | null;
  /** Set when the batch was created but its PDF could not be fetched — the codes still exist. */
  problem?: unknown;
}

/** Fetches the batch's PDF and hands it to a new tab. */
const openBatchPdf = (id: string) => openPdf(`/label-batches/${id}/pdf`);

function ResultLine({ result, at }: { result: PrintResult | null; at: ResultAt }) {
  if (!result || result.at !== at) return null;
  // The batch exists; only its PDF failed. Saying so is the whole point — otherwise the obvious
  // move is to press Print again and mint a second batch over the same stickers.
  if (result.problem)
    return (
      <div className={s.stFieldErr}>
        <ErrorNote error={result.problem} />
        <p className={s.stHelpLine}>{result.text}</p>
      </div>
    );
  return (
    <p className={s.stOk} role="status">
      {result.text}
      {result.pdf?.opened && result.withTab ? ' The PDF opened in a new tab.' : ''}
      {result.pdf && !result.pdf.opened && (
        <>
          {' '}
          Your browser blocked the new tab. <BlockedPdfLink key={result.pdf.url} pdf={result.pdf} />
        </>
      )}
    </p>
  );
}

export function Stickers() {
  const meQuery = useMe();
  const skusQuery = useSkus();
  const batchesQuery = useBatches();
  const orgQuery = useOrg();
  const qc = useQueryClient();
  const fieldId = useId();

  const [kind, setKind] = useState<StickerKind>('sku');
  const [sku, setSku] = useState<SkuDto | null>(null);
  // Kept as text so the field can be emptied while retyping; a blank field is simply not printable.
  const [qtyText, setQtyText] = useState('40');
  // The SKU and site the quantity was last typed for. Any other pair with a plan from the import
  // starts from that plan instead (runbook step 13: the old Sheet's count plus 10% spare).
  const [qtyTypedFor, setQtyTypedFor] = useState<string | null>(null);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [siteTouched, setSiteTouched] = useState(false);
  const [result, setResult] = useState<PrintResult | null>(null);
  const [voidTarget, setVoidTarget] = useState<LabelBatchDto | null>(null);

  const me = meQuery.data;
  const role = me?.role;
  const sites = useMemo(() => me?.sites ?? [], [me]);
  const skus = skusQuery.data ?? [];
  const batches = batchesQuery.data ?? [];
  // The picker offers live SKUs only; the table has to name a batch printed against a SKU that
  // has since been deactivated, and `useSkus()` would drop it — leaving a real SKU batch showing
  // `SkuTag`'s null state, the words "Blank sticker", in the table an admin voids and reprints from.
  const skuMap = useSkuMap();
  const siteMap = useMemo(() => new Map(sites.map((x) => [x.id, x])), [sites]);

  // Default to the first live site, without ever overriding a deliberate "No site".
  const defaultSiteId = useMemo(() => sites.find((x) => x.live)?.id ?? null, [sites]);
  const effectiveSiteId = siteTouched ? siteId : defaultSiteId;
  const site = effectiveSiteId ? siteMap.get(effectiveSiteId) : undefined;

  const maxQty = maxQtyFor(kind);
  const planPair = kind === 'sku' && sku && effectiveSiteId ? { siteId: effectiveSiteId, skuId: sku.id } : null;
  const plannedQuery = usePlannedQuantity(planPair?.siteId ?? null, planPair?.skuId ?? null);
  const planned = planPair ? (plannedQuery.data?.[0]?.qty ?? null) : null;
  const pairKey = planPair ? `${planPair.skuId}|${planPair.siteId}` : null;
  const withSpare = planned !== null ? plannedWithSpare(planned) : null;
  const fromPlan = withSpare !== null && withSpare > 0 && qtyTypedFor !== pairKey;
  const shownQty = fromPlan ? String(Math.min(maxQty, withSpare)) : qtyText;
  const qty = Math.floor(Number(shownQty)) || 0;
  const sizes = orgSizes(me?.org);
  const sizeLabel = stickerInches(kind === 'pallet' ? sizes.pallet : sizes.box);
  // The field can be empty mid-retype, and "Print 0 stickers" is not a thing anyone can do.
  const qtyWords = qty >= 1 ? `${qty} ${qty === 1 ? 'sticker' : 'stickers'}` : 'stickers';

  const print = useMutation({
    mutationFn: async (v: { body: Record<string, unknown>; at: ResultAt }) => {
      const batch = await api.post<LabelBatchDto>('/label-batches', v.body);
      // Past this line the batch exists and its codes are spent, whatever the PDF does. A failed
      // fetch is therefore reported as "created but not opened", never as a failure to create —
      // and `print.isError` keeps its honest meaning: the batch was not created at all.
      try {
        return { batch, pdf: (await openBatchPdf(batch.id)) as OpenedPdf | null, at: v.at, problem: null as unknown };
      } catch (problem) {
        return { batch, pdf: null, at: v.at, problem };
      }
    },
    // Settled, not success: a batch that was created has to appear in the table even when its PDF
    // never arrived, or the only visible move is to press Print again and burn a second run of codes.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.batches });
    },
    onSuccess: (r) => {
      setResult({
        at: r.at,
        text: r.problem
          ? `${r.batch.code} was created and its ${r.batch.qty} ${r.batch.qty === 1 ? 'code is' : 'codes are'} reserved. Use Print again on that batch below — printing here would create a second batch.`
          : `${r.batch.code} · ${r.batch.qty} ${r.batch.qty === 1 ? 'sticker' : 'stickers'} ready.`,
        withTab: true,
        pdf: r.pdf,
        problem: r.problem,
      });
    },
  });

  // A test label is not a sticker: no batch, no code, and no scan will receive it (`GET /test-label`).
  const testPrint = useMutation({
    mutationFn: (testKind: 'box' | 'pallet') => openPdf(`/test-label?kind=${testKind}${effectiveSiteId ? `&siteId=${effectiveSiteId}` : ''}`),
    onSuccess: (pdf) => setResult({ at: 'printer', text: 'Test label ready. It is marked TEST and is not a box. Check the size and alignment before a full run.', withTab: false, pdf }),
  });

  const reprint = useMutation({
    mutationFn: async (batch: LabelBatchDto) => ({ batch, pdf: await openBatchPdf(batch.id) }),
    onSuccess: (r) => {
      setResult({ at: 'batches', text: `${r.batch.code} · ${r.batch.unused} ${r.batch.unused === 1 ? 'sticker' : 'stickers'} ready.`, withTab: true, pdf: r.pdf });
    },
  });

  const voidBatch = useMutation({
    mutationFn: (batch: LabelBatchDto) => api.post<LabelBatchDto>(`/label-batches/${batch.id}/void`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.batches });
      setVoidTarget(null);
    },
  });

  if (!me) return meQuery.isError ? <ErrorNote error={meQuery.error} /> : <Spinner label="Loading stickers" />;
  if (!role || !can(role, 'labels')) return <Empty title="Stickers are printed by the office" />;

  function typeQty(text: string) {
    setQtyText(text);
    setQtyTypedFor(pairKey);
  }

  function chooseKind(next: StickerKind) {
    setKind(next);
    setResult(null);
    const limit = maxQtyFor(next);
    if (qty > limit) typeQty(String(limit));
  }

  function stepQty(by: number) {
    typeQty(String(Math.min(maxQty, Math.max(1, qty + by))));
  }

  /** `skuId` travels with every kind but `blank`, which the API refuses to give one. */
  const bodyFor = (kindForBody: StickerKind, qtyForBody: number) => ({
    kind: kindForBody,
    skuId: kindForBody === 'blank' ? null : (sku?.id ?? null),
    siteId: effectiveSiteId,
    qty: qtyForBody,
  });

  const needsSku = kind === 'sku';
  const canPrint = qty >= 1 && qty <= maxQty && (!needsSku || Boolean(sku)) && !print.isPending;

  function submit() {
    if (!canPrint) return;
    setResult(null);
    print.mutate({ body: bodyFor(kind, qty), at: 'form' });
  }

  /** One label to check size and alignment: a pallet one when the form prints pallets, else a box one, at the form's site. */
  function printTestLabel() {
    setResult(null);
    testPrint.reset();
    testPrint.mutate(kind === 'pallet' ? 'pallet' : 'box');
  }

  return (
    <div className={s.stPage}>
      {/* ---------- new batch ---------- */}
      <form
        className="panel"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="panel-h">
          <h2>New batch</h2>
        </div>
        <div className={s.stForm}>
          <div className="fld">
            <span id={`${fieldId}-kind`}>Sticker type</span>
            <div className={`seg ${s.stSeg}`} role="group" aria-labelledby={`${fieldId}-kind`}>
              {KINDS.map((k) => (
                <button key={k.value} type="button" className={kind === k.value ? 'on' : undefined} aria-pressed={kind === k.value} onClick={() => chooseKind(k.value)}>
                  {k.label}
                </button>
              ))}
            </div>
          </div>

          {kind !== 'blank' && (
            <div className="fld">
              <span>SKU{kind === 'pallet' ? ' (optional)' : ''}</span>
              {skusQuery.isLoadingError ? (
                <div className={s.stFieldErr}>
                  <ErrorNote error={skusQuery.error} />
                  <button type="button" className="btn sm" onClick={() => skusQuery.refetch()}>
                    Try again
                  </button>
                </div>
              ) : (
                <>
                  <SkuPicker skus={skus} sites={sites} value={sku} onSelect={setSku} lineTag={() => null} />
                  <RefetchNote queries={[skusQuery]} />
                </>
              )}
            </div>
          )}

          <div className="fld">
            <label htmlFor={`${fieldId}-qty`}>Quantity</label>
            <div className={s.stQtyRow}>
              <button type="button" className={s.stStep} aria-label="One sticker fewer" disabled={qty <= 1} onClick={() => stepQty(-1)}>
                −
              </button>
              <input
                id={`${fieldId}-qty`}
                type="number"
                className={`inp ${s.stQtyInput}`}
                min={1}
                max={maxQty}
                step={1}
                inputMode="numeric"
                value={shownQty}
                onChange={(e) => typeQty(e.target.value)}
              />
              <button type="button" className={s.stStep} aria-label="One sticker more" disabled={qty >= maxQty} onClick={() => stepQty(1)}>
                <Icon name="plus" />
              </button>
            </div>
            <span className={s.stHelpLine}>
              {kind === 'pallet'
                ? `One sticker per pallet. 1–${maxQty}.`
                : qty >= 1
                  ? `One sticker per box. Creates ${qty} ${qty === 1 ? 'box' : 'boxes'} as labeled. 1–${maxQty}.`
                  : `One sticker per box. 1–${maxQty}.`}
            </span>
            {planned !== null && withSpare !== null && (
              <span className={s.stHelpLine} role="note">
                {planned === 0
                  ? 'The import planned no boxes of this SKU here.'
                  : fromPlan
                    ? `Planned ${planned} from the import, plus 10% spare${withSpare > maxQty ? `. One batch holds up to ${maxQty}.` : ''}`
                    : `Planned ${planned} from the import: ${withSpare} with 10% spare.`}
              </span>
            )}
          </div>

          <div className="fld">
            <label htmlFor={`${fieldId}-site`}>Site</label>
            <div className={`inp ${s.stSelWrap}`}>
              <span className={s.stSelFace} aria-hidden="true">
                {site ? (
                  <>
                    <SiteTag site={site} />
                    {site.name}
                  </>
                ) : (
                  <span className="ph">No site</span>
                )}
              </span>
              <Icon name="chev-down" className="chev" />
              <select
                id={`${fieldId}-site`}
                className={s.stSel}
                value={effectiveSiteId ?? ''}
                onChange={(e) => {
                  setSiteTouched(true);
                  setSiteId(e.target.value || null);
                }}
              >
                {sites.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                    {x.live ? '' : ' (not live)'}
                  </option>
                ))}
                <option value="">No site</option>
              </select>
            </div>
          </div>

          <div className="fld">
            <span>Size</span>
            <div className="inp off">
              {sizeLabel}
              <span className="grow" />
              <span className="faint" style={{ fontSize: 12 }}>
                {kind === 'pallet' ? 'pallet' : 'box default'}
              </span>
            </div>
          </div>

          <p className={s.stNote}>
            <Icon name="printer" />
            Print through the laptop&rsquo;s thermal printer (Rollo, Zebra).
          </p>
        </div>

        <div className={s.stSum}>
          <div className={s.stSumRow}>
            <span>Output</span>
            <b>PDF · 1 sticker per page</b>
          </div>
          <div className={s.stSumRow}>
            <span>Codes</span>
            <b>Reserved when you print</b>
          </div>
        </div>

        <div className={s.stGo}>
          {print.isError && print.variables?.at === 'form' && <ErrorNote error={print.error} />}
          <button type="submit" className="btn primary lg" disabled={!canPrint}>
            <Icon name="printer" />
            Print {qtyWords}
          </button>
          {/* A disabled primary action has to say what is missing, or it reads as broken. */}
          {!canPrint && !print.isPending && (
            <span className={s.stHelpLine}>{needsSku && !sku ? 'Choose a SKU to print these stickers.' : `Enter a quantity between 1 and ${maxQty}.`}</span>
          )}
          <ResultLine result={result} at="form" />
        </div>
      </form>

      {/* ---------- preview + printer ---------- */}
      <div className={s.stCol}>
        <div className="panel">
          <div className="panel-h">
            <h2>Preview</h2>
            <Chip>To scale</Chip>
            <Chip>Sample</Chip>
            <span className="grow" />
            {/* "1 of 40" is what an admin checks before committing a roll: this is one of the N
                about to print, not a specimen of something else. */}
            <span className="sub">
              {qty >= 1 ? `1 of ${qty} · ` : ''}
              {KIND_WORDS[kind]} · black on white
            </span>
          </div>
          <div className={s.stMat}>
            <StickerPreview kind={kind} sku={kind === 'blank' ? null : sku} siteCode={site?.shortCode ?? null} timeZone={site?.timezone} boxSize={sizes.box} palletSize={sizes.pallet} />
            <div className={s.stCap}>
              <div>
                <b>QR holds the code</b>nothing else
              </div>
              <div>
                <b>Color is largest</b>match by eye
              </div>
              <div>
                <b>Black only</b>thermal print
              </div>
            </div>
          </div>
          <p className={s.stPvF}>
            <Icon name="lock" />
            Codes are reserved when you print, not before.
          </p>
        </div>

        <div className="panel">
          <div className="panel-h">
            <h2>Printer</h2>
            <span className="grow" />
            {role && can(role, 'settings') && (
              <Link className="link arrow" style={{ fontSize: 12.5 }} to="/office/settings/printing">
                Printing settings
              </Link>
            )}
          </div>
          <div className={s.stPrinter}>
            <div className={s.stSizeRow}>
              {/* The panel's whole job is to say what comes out of the printer, and the button
                  beneath it prints whatever the form is set to — including a 4 × 6 in pallet label. */}
              <span className="sec-k">Label size</span>
              <b>{sizeLabel}</b>
            </div>
            <ol className={s.stSteps}>
              <li>Connect the thermal printer to this laptop by USB and install its driver</li>
              <li>In the print dialog choose that printer, scale 100% and no margins</li>
              <li>Chrome remembers the printer, so this is a one-time setup</li>
            </ol>
            {testPrint.isError && <ErrorNote error={testPrint.error} />}
            <div className="row">
              <button type="button" className="btn" disabled={testPrint.isPending} onClick={printTestLabel}>
                <Icon name="printer" />
                Print test label
              </button>
            </div>
            <ResultLine result={result} at="printer" />
          </div>
        </div>
      </div>

      {/* ---------- recent batches ---------- */}
      <div className={`panel ${s.stWide}`}>
        <div className="panel-h">
          <h2>Recent batches</h2>
          <span className="grow" />
          {batchesQuery.isFetching && <Spinner label="Refreshing batches" />}
        </div>

        {reprint.isError && (
          <div className={s.stRowNote}>
            <ErrorNote error={reprint.error} />
          </div>
        )}
        <ResultLine result={result} at="batches" />

        <RefetchNote queries={[batchesQuery]} className={s.stRowNote} />
        {batchesQuery.isLoadingError ? (
          <div className={s.stTableErr}>
            <ErrorNote error={batchesQuery.error} />
            <button type="button" className="btn" onClick={() => batchesQuery.refetch()}>
              Try again
            </button>
          </div>
        ) : batchesQuery.isPending ? (
          <div className="panel-b">
            <Spinner label="Loading batches" />
          </div>
        ) : batches.length === 0 ? (
          <Empty title="No batches yet" description="Every batch you print is listed here, with what has been received and what is still unused." />
        ) : (
          <table className="t">
            <thead>
              <tr>
                <th>Code</th>
                <th>Kind</th>
                <th>SKU</th>
                <th>Site</th>
                <th className="r">Qty</th>
                <th className="r">Received</th>
                <th className="r">Unused</th>
                <th>Created</th>
                <th className="r">Actions</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const batchSite = b.siteId ? siteMap.get(b.siteId) : undefined;
                return (
                  <tr key={b.id}>
                    <td className="mono">
                      <span className="row" style={{ gap: 8 }}>
                        {b.code}
                        {b.voidedAt && <Chip tone="bad">Voided</Chip>}
                      </span>
                    </td>
                    <td>
                      <Chip>{KIND_WORDS[b.kind]}</Chip>
                    </td>
                    <td>{b.kind === 'blank' ? <span className="faint">—</span> : b.skuId ? <SkuTag sku={skuMap.get(b.skuId)} short withCode /> : <span className="faint">—</span>}</td>
                    <td>{batchSite ? <SiteTag site={batchSite} /> : <span className="faint">—</span>}</td>
                    <td className="r num">{b.qty}</td>
                    <td className="r num">{b.received}</td>
                    <td className="r num">{b.unused}</td>
                    <td className={s.stWhen}>
                      {zonedDateTime(b.createdAt, batchSite?.timezone ?? orgQuery.data?.timezone)}
                      <small>{b.createdByName}</small>
                    </td>
                    <td>
                      <div className={s.stActs}>
                        <button
                          type="button"
                          className="btn sm"
                          aria-label={`Print again ${b.code}`}
                          disabled={reprint.isPending}
                          onClick={() => {
                            setResult(null);
                            reprint.reset();
                            reprint.mutate(b);
                          }}
                        >
                          <Icon name="printer" />
                          Print again
                        </button>
                        <button
                          type="button"
                          className="btn sm danger"
                          aria-label={`Void ${b.code}`}
                          disabled={b.unused === 0}
                          onClick={() => {
                            voidBatch.reset();
                            setVoidTarget(b);
                          }}
                        >
                          Void
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={Boolean(voidTarget)}
        onOpenChange={(open) => {
          if (!open) setVoidTarget(null);
        }}
        title="Void unused stickers"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setVoidTarget(null)}>
              Cancel
            </button>
            <button type="button" className="btn danger" disabled={voidBatch.isPending} onClick={() => voidTarget && voidBatch.mutate(voidTarget)}>
              Void {voidTarget?.unused ?? 0} stickers
            </button>
          </>
        }
      >
        {voidBatch.isError && <ErrorNote error={voidBatch.error} />}
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.45 }}>
          Void {voidTarget?.unused ?? 0} unused stickers from {voidTarget?.code}? They can never be used.
        </p>
      </Modal>
    </div>
  );
}
